import type { Announcement } from "@brookbush/types";

import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";

import Button from "~/components/Button";
import Container from "~/components/Container";
import DottedBackground from "~/components/DottedBackground";
import TextBlock from "~/components/TextBlock";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.server";

interface AnnouncementPageProps {
	announcement: Announcement;
}

const AnnouncementPage: NextPageWithProps<AnnouncementPageProps> = ({
	announcement,
}) => {
	return (
		<DottedBackground>
			<Container className="!max-w-3xl !py-4 tablet:!py-12 flex flex-col gap-6 tablet:gap-10">
				<div className="flex flex-col gap-2 tablet:gap-6">
					<h1 className="font-bold tablet:text-4xl text-xl">
						{announcement.title}
					</h1>

					<p className="tablet:text-lg">
						{new Date(announcement._createdAt).toLocaleDateString("en-US", {
							dateStyle: "medium",
						})}
					</p>
				</div>

				<TextBlock value={announcement.body} />

				<Link className="mx-auto" href={announcement.callToAction.url}>
					<Button className="w-full min-w-56 rounded-full">
						{announcement.callToAction.text}
					</Button>
				</Link>
			</Container>
		</DottedBackground>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	return {
		paths: [],
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	const slug = ctx.params?.slug as string;

	try {
		const announcement: Announcement = await fetcher(`/announcements/${slug}`);

		if (!announcement) {
			return { notFound: true };
		}

		return {
			props: {
				announcement,
				breadcrumbs: [
					{ name: "Announcements", href: "/announcements" },
					{
						name: announcement.title,
						href: `/announcements/${announcement.slug}`,
					},
				],
			},
		};
	} catch (_error) {
		return { notFound: true };
	}
};

export default AnnouncementPage;
